/// <amd-module name="VSSUI/Components/Pill/Pill" />
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { Button } from "VSSUI/Button";
import { FocusZoneContext, IFocusZoneContext } from "VSSUI/FocusZone";
import { Icon, IconSize } from "VSSUI/Icon";
import * as Resources from "VSSUI/Resources.Widgets";
import { css, getSafeId, KeyCode } from "VSSUI/Util";
import { darken, getColorString, IColor, isDark } from "VSSUI/Utilities/Color";
import { getTabIndex } from "VSSUI/Utilities/Focus";
import { IPillProps, PillSize, PillVariant } from "./Pill.Props";

export interface IPillState {
    isHoveringPrimaryElement: boolean;
}

export class Pill extends React.Component<IPillProps, IPillState> {
    private static getColorStyle(
        color: IColor | undefined,
        isHoveringPrimaryElement: boolean,
        onClick?: (event?: React.MouseEvent<HTMLDivElement>) => void
    ) {
        if (!color) {
            return undefined;
        }

        const renderColor = onClick && isHoveringPrimaryElement ? darken(color, 0.06) : color;
        return { backgroundColor: getColorString(renderColor) };
    }

    private static getSizeClass(size: PillSize): string {
        switch (size) {
            case PillSize.compact:
                return "compact";
            case PillSize.large:
                return "large";
            case PillSize.regular:
            default:
                return "regular";
        }
    }

    private static getVariantClass(variant: PillVariant, color?: IColor): string {
        switch (variant) {
            case PillVariant.outlined:
                return "outlined";
            case PillVariant.colored:
                if (color) {
                    return css("colored", isDark(color) ? "dark" : "light");
                } else {
                    return "standard";
                }
            case PillVariant.themedStandard:
                return "themed-standard";
            case PillVariant.standard:
            default:
                return "standard";
        }
    }

    public static getDerivedStateFromProps(props: IPillProps, state: IPillState) {
        if ($DEBUG) {
            const { color, iconProps, onRenderFilledVisual, size = PillSize.regular, variant } = props;

            // Checking for unsupported compact fields and warning if there are any
            if (size === PillSize.compact) {
                const unsupportedFields = [];
                onRenderFilledVisual && unsupportedFields.push("onRenderFilledVisual");

                if (unsupportedFields.length > 0) {
                    console.warn(
                        `Pill Size is Compact, but the following fields were provided: ${unsupportedFields.join(
                            ", "
                        )} - these will be ignored. Consider changing Pill Size to Regular or Large if you need to support these items`
                    );
                }
            } else {
                if (onRenderFilledVisual && iconProps) {
                    console.warn("onRenderFilledVisual and iconProps have both been supplied; using onRenderFilledVisual");
                }
            }

            if (variant === PillVariant.colored && !color) {
                console.warn("Pill Variant is set to Colored, but not color was provided - Pill will render as Standard");
            } else if (color && variant !== PillVariant.colored) {
                console.warn("Color was provided, but Pill Variant is not set to Colored - Pill will render as whatever variant was provided");
            }
        }

        return state;
    }
    constructor(props: IPillProps) {
        super(props);

        this.state = {
            isHoveringPrimaryElement: false
        };
    }

    public render() {
        const {
            ariaHidden,
            contentClassName,
            className,
            color,
            containsCount = false,
            iconProps,
            id,
            onClick,
            onBlur,
            onFocus,
            onRemoveClick,
            onRenderFilledVisual,
            size = PillSize.regular,
            variant = PillVariant.standard
        } = this.props;
        const { isHoveringPrimaryElement } = this.state;
        const ariaLabel = this.props.ariaLabel || this.getChildText();

        return (
            <FocusZoneContext.Consumer>
                {(zoneContext: IFocusZoneContext) => (
                    <div
                        className={css(
                            className,
                            "bolt-pill flex-row flex-center",
                            Pill.getVariantClass(variant, color),
                            Pill.getSizeClass(size),
                            containsCount && "count",
                            isHoveringPrimaryElement && "hover",
                            onClick && "clickable",
                            onRenderFilledVisual && "has-filled-visual",
                            iconProps && !onRenderFilledVisual && "has-icon",
                            onRemoveClick && "has-remove-button"
                        )}
                        id={getSafeId(id)}
                        onClick={onClick}
                        onBlur={onBlur}
                        onMouseEnter={this.onMouseEnter}
                        onMouseLeave={this.onMouseLeave}
                        style={Pill.getColorStyle(color, isHoveringPrimaryElement, onClick)}
                    >
                        {onRenderFilledVisual && <div className="bolt-pill-filled-visual flex-noshrink">{onRenderFilledVisual()}</div>}
                        {iconProps && !onRenderFilledVisual && <Icon {...iconProps} className={css(iconProps.className, "bolt-pill-icon")} />}
                        <div
                            aria-label={ariaLabel}
                            aria-hidden={ariaHidden}
                            className={css(contentClassName, "bolt-pill-content text-ellipsis")}
                            data-focuszone={!this.props.excludeFocusZone ? zoneContext.focuszoneId : undefined}
                            onFocus={onFocus}
                            onKeyDown={this.onKeyDown}
                            role={onClick ? "button" : "presentation"}
                            tabIndex={onClick || onFocus ? getTabIndex(this.props) : undefined}
                        >
                            {this.props.children}
                        </div>
                        {onRemoveClick && (
                            <Button
                                ariaLabel={format(Resources.RemovePillLabel, ariaLabel)}
                                className="bolt-pill-button"
                                iconProps={{ iconName: "Cancel", size: IconSize.inherit }}
                                onClick={onRemoveClick}
                                onMouseLeave={this.onMouseLeaveButton}
                                onMouseOver={this.onMouseOverButton}
                                subtle={true}
                                tooltipProps={{ text: format(Resources.RemovePillLabel, ariaLabel) }}
                                tabIndex={this.props.removeButtonTabIndex}
                            />
                        )}
                    </div>
                )}
            </FocusZoneContext.Consumer>
        );
    }

    private getChildText = () => {
        let text = "";
        React.Children.map(this.props.children, child => {
            if (typeof child === "string") {
                text += child;
            }
        });
        return text;
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const keyCode = event.which;

        if (keyCode === KeyCode.enter) {
            this.props.onClick && this.props.onClick();
        }
    };

    private onMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
        this.props.onMouseEnter && this.props.onMouseEnter(event);
        this.setState({
            isHoveringPrimaryElement: true
        });
    };

    /**
     * onMouseLeaveButton fires first; if it leaves the container too
     * onMouseLeave will setState again, which will prevent weird behavior
     */
    private onMouseLeave = (event: React.MouseEvent<HTMLDivElement>) => {
        this.props.onMouseLeave && this.props.onMouseLeave(event);
        this.setState({
            isHoveringPrimaryElement: false
        });
    };

    private onMouseLeaveButton = () => {
        this.setState({
            isHoveringPrimaryElement: true
        });
    };

    private onMouseOverButton = () => {
        this.setState({
            isHoveringPrimaryElement: false
        });
    };
}
